import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { type Permission, effectivePermissions, effectiveRole } from "@astroplanet/shared";
import { setLocale } from "../i18n";
import { api, getBranch, loadBranch, loadToken, setBranch, setToken, setUnauthorizedHandler } from "../lib/api";
import { isV2 } from "../lib/config";
import { applyLabels, type Labels } from "../lib/labels";
import type { Branch, Locale, User } from "../lib/types";

interface AuthApi {
  user: User | null;
  /** True while the stored token is being validated on startup. */
  booting: boolean;
  isManager: boolean;
  /** v2: owners and admins see every branch and the consolidated views. */
  isAdmin: boolean;
  /** May this person open that page / use that feature? (v1 and old servers: the defaults of their role.) */
  can: (p: Permission) => boolean;
  /** v2 login names the company (tenant) as well. */
  login: (username: string, pin: string, tenant?: string) => Promise<void>;
  logout: () => void;
  /** Switches the UI language and remembers it on the staff profile. */
  changeLocale: (locale: Locale) => Promise<void>;
  /** v2: the branch this terminal works in. `null` until chosen (staff with several branches pick one). */
  branch: Branch | null;
  selectBranch: (id: string) => void;
  /** v2: signed in but no branch chosen yet. */
  needsBranch: boolean;
}

const Ctx = createContext<AuthApi | null>(null);
const BRANCHES_KEY = "astro.branches";

const readBranches = (): Branch[] => {
  try {
    return JSON.parse(localStorage.getItem(BRANCHES_KEY) ?? "[]") as Branch[];
  } catch {
    return [];
  }
};
const writeBranches = (b: Branch[]) => {
  try {
    localStorage.setItem(BRANCHES_KEY, JSON.stringify(b));
  } catch {
    /* storage unavailable */
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const qc = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [branch, setBranchState] = useState<Branch | null>(null);
  const [booting, setBooting] = useState(true);

  const logout = useCallback(() => {
    setToken(null);
    setBranch(null);
    setUser(null);
    setBranchState(null);
    qc.clear();
  }, [qc]);

  useEffect(() => {
    setUnauthorizedHandler(logout);
    return () => setUnauthorizedHandler(null);
  }, [logout]);

  useEffect(() => {
    if (!loadToken()) {
      setBooting(false);
      return;
    }
    const v2 = isV2();
    if (v2) loadBranch();
    api
      .get<{ user: User; branch?: Branch; labels?: Labels }>("/auth/me")
      .then((r) => {
        applyLabels(r.labels);
        const branches = v2 ? readBranches() : undefined;
        setUser({ ...r.user, branches });
        if (v2 && r.branch) setBranchState(r.branch);
      })
      .catch(() => {
        setToken(null);
        setBranch(null);
      })
      .finally(() => setBooting(false));
  }, []);

  // Permissions can change while someone is signed in: pick the change up every minute and when the window gets focus again
  useEffect(() => {
    if (!user || !isV2()) return;
    const refresh = () => {
      api.get<{ user: User }>("/auth/me").then((r) => {
        setUser((u) => (u && (u.role !== r.user.role || JSON.stringify(u.permissions) !== JSON.stringify(r.user.permissions) || u.name !== r.user.name) ? { ...u, role: r.user.role, permissions: r.user.permissions, name: r.user.name } : u));
      }).catch(() => undefined);
    };
    const t = setInterval(refresh, 60_000);
    window.addEventListener("focus", refresh);
    return () => { clearInterval(t); window.removeEventListener("focus", refresh); };
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (username: string, pin: string, tenant?: string) => {
    const r = await api.post<{ token: string; user: User; labels?: Labels }>("/auth/login", isV2() ? { tenant: tenant ?? "", username, pin } : { username, pin });
    setToken(r.token);
    applyLabels(r.labels);
    setUser(r.user);
    if (isV2()) {
      const branches = r.user.branches ?? [];
      writeBranches(branches);
      // one branch = no question to ask; otherwise reuse the last choice if it is still allowed
      const saved = getBranch() ?? loadBranch();
      const pick = branches.length === 1 ? branches[0] : branches.find((b) => b.id === saved);
      setBranch(pick?.id ?? null);
      setBranchState(pick ?? null);
    }
    // Respect the staff member's saved language.
    await setLocale(r.user.locale);
  }, []);

  const selectBranch = useCallback(
    (id: string) => {
      const b = user?.branches?.find((x) => x.id === id) ?? null;
      setBranch(b?.id ?? null);
      setBranchState(b);
      qc.clear(); // everything on screen belonged to the other branch
    },
    [user, qc],
  );

  const changeLocale = useCallback(
    async (locale: Locale) => {
      await setLocale(locale);
      if (user) {
        setUser({ ...user, locale });
        api.patch("/auth/me/locale", { locale }).catch(() => undefined);
      }
    },
    [user],
  );

  const perms = useMemo(() => (user ? effectivePermissions(user.role, user.permissions ?? null) : []), [user]);
  const value = useMemo<AuthApi>(
    () => ({
      user, booting, login, logout, changeLocale, branch, selectBranch,
      isManager: !!user && effectiveRole(user.role, perms) !== "cashier",
      isAdmin: user?.role === "admin" || user?.role === "owner",
      can: (p: Permission) => perms.includes(p),
      needsBranch: isV2() && !!user && !branch,
    }),
    [user, perms, booting, login, logout, changeLocale, branch, selectBranch],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthApi {
  const v = useContext(Ctx);
  if (!v) throw new Error("useAuth must be used inside <AuthProvider>");
  return v;
}
